'use client';
import { useInfiniteScroll } from 'ahooks';
import { useTranslations } from 'next-intl';
import { type FC, Fragment, useEffect, useRef, useState } from 'react';
import { BaseLineInfo } from '@/app/components/icons/BaseLineInfo';
import { BaseLineRight } from '@/app/components/icons/BaseLineRight';
import { BaseLineSwap } from '@/app/components/icons/BaseLineSwap';
import { AudioDom } from '@/app/components/shared/AudioDom';
import { AudioSelectionDialog } from '@/app/components/shared/AudioSelectionDialog';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { DisplaySelectedAudioItem } from '@/app/components/shared/DisplaySelectedAudioItem';
import { GlobalLoader } from '@/app/components/shared/GlobalLoader';
import { SearchEmpty } from '@/app/components/shared/SearchEmpty';
import { Button } from '@/app/components/ui/button';
import { DialogTitle } from '@/app/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/app/components/ui/tabs';
import { SEARCH_EMPTY_ICON, SITE } from '@/app/constants';
import type { AgentVoiceLanguageType, AgentVoiceOption } from '@/app/types';
import { cn } from '@/app/utils';
import { useLocale } from '@/i18n/routing';
import { useGetVoiceLanguage } from '../../_hooks/use-get-voice-language';
import { agentListVoices } from '../../apis';
import { LimitedTag } from '../LimitedTag';

const PAGE_SIZE = 100;

type AgentListVoicesData = {
  list: AgentVoiceOption[];
  nextPage: number;
};
/**
 * 将中文描述转换为对应语言描述
 * @param description 中文描述
 * @returns 英文描述
 */
const transformDescription = (description?: string) => {
  if (SITE === 'global') {
    switch (description) {
      case '中文普通话':
        return 'Chinese Mandarin';
      case '中文粤语':
        return 'Chinese Cantonese';
      default:
        return description;
    }
  }
  return description;
};

const getAudioList: ({
  language,
  page,
  locale,
}: {
  locale: string;
  language: AgentVoiceLanguageType;
  page?: number;
}) => Promise<AgentListVoicesData> = ({ language, page = 1 }) =>
  new Promise((res) => {
    agentListVoices({
      query: {
        ...(language === 'all' ? {} : { language }),
        'pager.page': page,
        'pager.pagesz': PAGE_SIZE,
      },
    })
      .then(({ data, error }) => {
        if (error) {
          res({
            list: [],
            nextPage: page + 1,
          });
        } else {
          const list: AgentVoiceOption[] = (data.voices || []).map((it) => {
            return {
              id: it.id,
              name: it.name,
              description: it.language === 'en' ? transformDescription(it?.description) : it?.description,
              img: it.avatar_url,
              url: it.sample_url,
              language: it.language === 'unspecified' ? 'en' : (it.language as AgentVoiceLanguageType),
            };
          });

          const hasMore = (list?.length || 0) >= PAGE_SIZE;

          res({
            list,
            nextPage: hasMore ? page + 1 : 1,
          });
        }
      })
      .catch((_e) => {
        res({
          list: [],
          nextPage: 1,
        });
      });
  });

const SelectAudioItem: FC<{
  audio: AgentVoiceOption;
  handleSelectAudio: (audio: AgentVoiceOption) => void;
  active: boolean;
  languageName?: string;
}> = (props) => {
  const { audio, handleSelectAudio, active, languageName } = props;
  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-12 border border-transparent px-3 py-2 transition-colors hover:bg-system-white04',
        active && 'rounded-12 border-system-white08 bg-system-white08',
      )}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        handleSelectAudio(audio);
      }}
    >
      <AudioDom audio={audio} className="size-12" />
      <div className="flex-1 overflow-hidden">
        <CustomTooltip
          content={
            <div className="flex items-center">
              {audio.name}
              {languageName ? ` - ${languageName}` : ''}
            </div>
          }
          side="top"
          sideOffset={4}
          showOnlyOnOverflow={true}
          maxLines={1}
        >
          <div className="text-sm text-system-white leading-[1.6]">
            {audio.name}
            {languageName ? ` - ${languageName}` : ''}
          </div>
        </CustomTooltip>
        {audio.description && (
          <CustomTooltip content={audio.description} side="top" sideOffset={4} showOnlyOnOverflow={true} maxLines={1}>
            <div className="text-system-white32 text-xs leading-normal">{audio.description}</div>
          </CustomTooltip>
        )}
      </div>
    </div>
  );
};

export const ChooseAudioDialog = ({
  changeAudio,
  selectedAudio,
  initialVoiceId,
  triggerVariant = 'default',
  triggerClassName,
  triggerAudioClassName,
  triggerTextClassName,
  triggerIconClassName,
  showAutoOption = true,
}: {
  changeAudio: (audio: AgentVoiceOption) => void;
  selectedAudio: AgentVoiceOption;
  initialVoiceId?: string;
  triggerVariant?: 'default' | 'compact';
  triggerClassName?: string;
  triggerAudioClassName?: string;
  triggerTextClassName?: string;
  triggerIconClassName?: string;
  showAutoOption?: boolean;
}) => {
  const t = useTranslations();
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const locale = useLocale();
  const { list: languageList, autoAudioItem, getLanguageName } = useGetVoiceLanguage();
  const [tempAudio, setTempAudio] = useState<AgentVoiceOption>(selectedAudio);
  const [language, setLanguage] = useState<AgentVoiceLanguageType>(tempAudio.language);
  const hasInitializedAudioOnOpenRef = useRef(false);

  const { data: listData, loading: firstLoading } = useInfiniteScroll<AgentListVoicesData>(
    (d) => getAudioList({ language, page: d?.nextPage, locale }),
    {
      target: ref,
      isNoMore: (d) => (d?.nextPage ?? 1) === 1,
      reloadDeps: [language],
      threshold: 100,
    },
  );

  // 初始化
  useEffect(() => {
    if (listData?.list?.length && !hasInitializedAudioOnOpenRef.current) {
      // 如果有 initialVoiceId 则取对应的音色，否则 能用auto用auto，不能用auto 取第一个音色
      hasInitializedAudioOnOpenRef.current = true;
      const firstAudio = triggerVariant === 'default' ? autoAudioItem : listData?.list?.[0];
      const matchedAudio = listData?.list?.find((audio) => audio.id === initialVoiceId) || firstAudio;
      setTempAudio(matchedAudio);
      changeAudio(matchedAudio);
    }
  }, [listData, initialVoiceId]);

  const handleSelectAudio = (audio: AgentVoiceOption) => {
    setTempAudio(audio);
  };

  const triggerDisplayAudio = selectedAudio;

  const renderAudioItem = (audio: AgentVoiceOption, className?: string) => {
    return (
      <div key={audio.id} className={cn('cursor-pointer last:mb-0', className)}>
        <SelectAudioItem
          audio={audio}
          handleSelectAudio={handleSelectAudio}
          active={audio.id === tempAudio.id}
          languageName={language === 'all' && audio.language !== 'all' ? getLanguageName(audio.language) : undefined}
        />
      </div>
    );
  };

  return (
    <AudioSelectionDialog
      open={isOpen}
      onOpenChange={setIsOpen}
      triggerAsChild
      trigger={
        <button
          type="button"
          className={cn(
            triggerVariant === 'compact'
              ? 'flex h-6 items-center gap-1 rounded-full bg-system-white08 py-1 pr-2 pl-1'
              : 'flex w-full items-center justify-between rounded-8 bg-system-black16 p-2',
            triggerClassName,
          )}
        >
          {triggerVariant === 'compact' ? (
            <>
              <AudioDom
                audio={triggerDisplayAudio}
                className={cn(
                  'size-[14px] rounded-[4px] border-[0.292px] border-system-white08',
                  triggerAudioClassName,
                )}
                playIconClassName="size-[7px]"
              />
              <span
                className={cn(
                  'max-w-[96px] truncate text-[10px] text-system-white leading-[1.4]',
                  triggerTextClassName,
                )}
              >
                {triggerDisplayAudio.name}
              </span>
              <BaseLineSwap className={cn('size-3 text-system-white64', triggerIconClassName)} />
            </>
          ) : (
            <>
              <DisplaySelectedAudioItem audio={triggerDisplayAudio} className="rounded-8 bg-system-white04" />
              <BaseLineRight className={cn('size-4', triggerIconClassName)} />
            </>
          )}
        </button>
      }
      header={
        <DialogTitle className="flex items-center gap-2 px-4 pr-20 font-semibold text-base leading-[1.4]">
          <CustomTooltip
            content={t('agentProject.voiceSelect.title')}
            side="top"
            sideOffset={4}
            showOnlyOnOverflow={true}
            maxLines={1}
            variant="glass"
          >
            {t('agentProject.voiceSelect.title')}
          </CustomTooltip>
          <CustomTooltip content={t('agentProject.voiceSelect.tipTooltip')} side="top" sideOffset={4}>
            <BaseLineInfo className="size-4 flex-shrink-0 text-system-white" />
          </CustomTooltip>
          <LimitedTag className="text-nowrap" />
          <div className="ml-2 flex flex-shrink-0 items-center gap-2 text-sm text-system-white48">
            {t('agentProject.voiceSelect.selected')}
            {tempAudio.language !== 'all' && `｜${getLanguageName(tempAudio.language)}`}
            <div className="flex items-center gap-2 rounded-full border border-system-white16 bg-system-white08 pr-4 pl-[1px]">
              <AudioDom audio={tempAudio} className="size-4" playIconClassName="size-2" />
              <div className="truncate text-system-white text-xs leading-[1.6]">{tempAudio.name}</div>
            </div>
          </div>
        </DialogTitle>
      }
      footer={
        <>
          <Button
            className="h-auto bg-system-white08 px-4 py-2 font-normal text-sm text-system-white leading-[1.4] hover:bg-system-white24"
            onClick={() => setIsOpen(false)}
          >
            {t('common.dialog.cancel')}
          </Button>
          <Button
            className="h-auto bg-system-white px-10 py-2 font-normal text-sm text-system-black leading-[1.4]"
            onClick={() => {
              changeAudio(tempAudio);
              setIsOpen(false);
            }}
          >
            {t('common.dialog.confirm')}
          </Button>
        </>
      }
    >
      <div className="mt-4 flex flex-1 overflow-hidden border-system-white04 border-t">
        <Tabs
          className="border-system-white04 border-r"
          orientation="vertical"
          value={language}
          onValueChange={(value) => setLanguage(value as AgentVoiceLanguageType)}
        >
          <TabsList className="flex h-full flex-col items-stretch justify-start gap-2 overflow-y-auto bg-transparent p-4">
            {languageList.map((item) => (
              <TabsTrigger
                key={item.id}
                value={item.id}
                className="block min-w-[130px] rounded-8 px-4 py-2 text-left font-normal text-sm text-system-white leading-[1.4] hover:bg-system-white04 data-[state=active]:bg-system-white12"
              >
                {item.name}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div
          className={cn('hide-scrollbar flex-1', (firstLoading || (listData?.list?.length || 0) === 0) && 'relative')}
        >
          {firstLoading ? (
            <GlobalLoader className="top-1/2" />
          ) : listData?.list?.length ? (
            <div className="grid max-h-full grid-cols-2 gap-x-6 gap-y-3 overflow-y-scroll p-4" ref={ref}>
              {listData?.list?.map((audio, index) => {
                return (
                  <Fragment key={audio.id}>
                    {showAutoOption && language === 'all' && index === 0 && renderAudioItem(autoAudioItem)}
                    {renderAudioItem(audio)}
                  </Fragment>
                );
              })}
            </div>
          ) : (
            <SearchEmpty
              emptySrc={SEARCH_EMPTY_ICON.selector}
              textClassName="text-xs"
              emptyText={t('agentProject.noRecord')}
            />
          )}
        </div>
      </div>
    </AudioSelectionDialog>
  );
};
